import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Loader2, CheckCircle, XCircle, Database, ChevronDown, ChevronUp, X
} from 'lucide-react';
import axios from 'axios';

export const ImportJobsIndicator = () => {
  const { API } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/assets/bulk-import/jobs`);
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch import jobs:', error);
    }
  }, [API]);

  const hasActiveJobs = jobs.some(j => j.status === 'processing');
  
  // Initial fetch on mount
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);
  
  // Set up polling interval based on job status
  useEffect(() => {
    let interval;
    if (hasActiveJobs) {
      // Poll every 3 seconds when jobs are active
      interval = setInterval(fetchJobs, 3000);
    } else if (jobs.length > 0) {
      // Poll every 60 seconds when there are completed/failed jobs (rare check)
      interval = setInterval(fetchJobs, 60000);
    }
    // If no jobs at all, don't poll
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [hasActiveJobs, jobs.length, fetchJobs]);

  const activeJobs = jobs.filter(j => j.status === 'processing');
  const recentJobs = jobs.filter(j => j.status !== 'processing').slice(0, 3);

  // Don't render anything if no jobs
  if (jobs.length === 0) {
    return null;
  }

  // Show indicator only if there are active jobs or recently completed ones

  return (
    <div className="relative">
      {/* Indicator Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setExpanded(!expanded)}
        className={`relative ${hasActiveJobs ? 'text-sky-600 dark:text-sky-400' : 'text-slate-500'}`}
        data-testid="import-jobs-indicator"
      >
        <Database className="w-4 h-4" />
        {hasActiveJobs && (
          <>
            <Loader2 className="w-3 h-3 ml-1 animate-spin" />
            <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-sky-500">
              {activeJobs.length}
            </Badge>
          </>
        )}
        {expanded ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
      </Button>

      {/* Dropdown Panel */}
      {expanded && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-50">
          <div className="p-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <span className="font-medium text-slate-900 dark:text-white text-sm">Import Jobs</span>
            <Button variant="ghost" size="sm" onClick={() => setExpanded(false)} className="h-6 w-6 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            {activeJobs.length > 0 && (
              <div className="p-2">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Active</p>
                {activeJobs.map(job => (
                  <JobItem key={job.job_id} job={job} />
                ))}
              </div>
            )}
            
            {recentJobs.length > 0 && (
              <div className="p-2 border-t border-slate-100 dark:border-slate-700">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Recent</p>
                {recentJobs.map(job => (
                  <JobItem key={job.job_id} job={job} />
                ))}
              </div>
            )}
            
            {jobs.length === 0 && (
              <div className="p-4 text-center text-sm text-slate-500">
                No import jobs
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const JobItem = ({ job }) => {
  const isActive = job.status === 'processing';
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed';
  
  return (
    <div className="p-2 rounded-md bg-slate-50 dark:bg-slate-700/50 mb-2">
      <div className="flex items-center gap-2 mb-1">
        {isActive && <Loader2 className="w-3 h-3 animate-spin text-sky-500" />}
        {isCompleted && <CheckCircle className="w-3 h-3 text-emerald-500" />}
        {isFailed && <XCircle className="w-3 h-3 text-red-500" />}
        <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
          {isActive ? 'Importing...' : isCompleted ? 'Completed' : 'Failed'}
        </span>
        <span className="text-xs text-slate-400 ml-auto">
          {job.processed}/{job.total}
        </span>
      </div>
      {isActive && (
        <Progress value={job.progress_percent} className="h-1" />
      )}
      <div className="text-[10px] text-slate-400 mt-1">
        Started: {new Date(job.started_at).toLocaleTimeString()}
      </div>
    </div>
  );
};

export default ImportJobsIndicator;
